/**
 * WordPress dependencies
 */
import { Button, Flex, FlexBlock, FlexItem, Popover, RangeControl } from '@wordpress/components';

import { withInstanceId } from '@wordpress/compose';
import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

/**
 * Internal dependencies
 */
import { BOX_SHADOW_POSITION, PREFIX } from '../../constants';
import ButtonsGroupControl from '../buttons-group';
import ResLabelControl from '../res-label-control';
import ResetButton from '../reset-btn';
import ColorControl from '../color-control';

const BoxShadowControl = ({ instanceId, label, controlName, objAttrs }) => {
    const [boxshadowPanel, setBoxshadowPanel] = useState(false);

    const id = `boxshadow-control-${instanceId}`;

    const { attributes, setAttributes } = objAttrs;

    const {
        [`${PREFIX}${controlName}BoxShadowPosition`]: boxShadowPosition = 'outset',
        [`${PREFIX}${controlName}BoxShadowColor`]: boxShadowColor,
        [`${PREFIX}${controlName}BoxShadowHorizontal`]: boxShadowHorizontal,
        [`${PREFIX}${controlName}BoxShadowVertical`]: boxShadowVertical,
        [`${PREFIX}${controlName}BoxShadowBlur`]: boxShadowBlur,
        [`${PREFIX}${controlName}BoxShadowSpread`]: boxShadowSpread
    } = attributes;

    return (
        <div className="faqmate-control-container box-shadow-control">
            <Flex>
                <FlexBlock>
                    <ResLabelControl
                        label={label}
                        requiredProps={{
                            id,
                            setAttributes,
                            attributes
                        }}
                        noResBtns={true}
                    />
                </FlexBlock>
                <FlexItem>
                    <Button
                        icon="image-rotate"
                        label={__('Reset', 'faqmate-blocks')}
                        onClick={() => {
                            setAttributes({
                                [`${PREFIX}${controlName}BoxShadowPosition`]: 'outset',
                                [`${PREFIX}${controlName}BoxShadowColor`]: '',
                                [`${PREFIX}${controlName}BoxShadowHorizontal`]: '',
                                [`${PREFIX}${controlName}BoxShadowVertical`]: '',
                                [`${PREFIX}${controlName}BoxShadowBlur`]: '',
                                [`${PREFIX}${controlName}BoxShadowSpread`]: ''
                            });
                        }}
                        className={`faqmate-reset-button range-btn ${
                            boxShadowColor || boxShadowHorizontal || boxShadowVertical || boxShadowSpread || boxShadowBlur
                                ? 'faqmate-reset-button'
                                : 'disabled'
                        }`}
                    />
                    <Button className="shadow-indicator" onClick={() => setBoxshadowPanel(true)} icon="admin-appearance" />
                    {boxshadowPanel && (
                        <Popover position="bottom right" onFocusOutside={() => setBoxshadowPanel(false)} offset={10}>
                            <div className="faqmate-shadow-panel">
                                <div className="position">
                                    <div className="faqmate-mb-8">
                                        <ResLabelControl
                                            label={__('Position', 'faqmate-blocks')}
                                            requiredProps={{
                                                id,
                                                setAttributes,
                                                attributes
                                            }}
                                            noResBtns={true}
                                        />
                                    </div>
                                    <ButtonsGroupControl
                                        value={boxShadowPosition}
                                        onChange={data =>
                                            setAttributes({
                                                [`${PREFIX}${controlName}BoxShadowPosition`]: data
                                            })
                                        }
                                        options={BOX_SHADOW_POSITION}
                                        hasIcons={false}
                                    />
                                </div>
                                <div className="box-shadow-color-picker">
                                    <ColorControl
                                        label={__('Color', 'faqmate-blocks')}
                                        color={boxShadowColor}
                                        onChange={value =>
                                            setAttributes({
                                                [`${PREFIX}${controlName}BoxShadowColor`]: value
                                            })
                                        }
                                    />
                                </div>
                                <div className="faqmate-mb-16">
                                    <ResLabelControl
                                        label={__('Horizontal', 'faqmate-blocks')}
                                        requiredProps={{
                                            id,
                                            setAttributes,
                                            attributes
                                        }}
                                        noResBtns={true}
                                    />
                                    <ResetButton
                                        onReset={() =>
                                            setAttributes({
                                                [`${PREFIX}${controlName}BoxShadowHorizontal`]: ''
                                            })
                                        }
                                        value={boxShadowHorizontal}
                                    >
                                        <RangeControl
                                            value={boxShadowHorizontal}
                                            onChange={data =>
                                                setAttributes({
                                                    [`${PREFIX}${controlName}BoxShadowHorizontal`]: data
                                                })
                                            }
                                            min={-100}
                                            max={100}
                                        />
                                    </ResetButton>
                                </div>
                                <div className="faqmate-mb-16">
                                    <ResLabelControl
                                        label={__('Vertical', 'faqmate-blocks')}
                                        requiredProps={{
                                            id,
                                            setAttributes,
                                            attributes
                                        }}
                                        noResBtns={true}
                                    />
                                    <ResetButton
                                        onReset={() =>
                                            setAttributes({
                                                [`${PREFIX}${controlName}BoxShadowVertical`]: ''
                                            })
                                        }
                                        value={boxShadowVertical}
                                    >
                                        <RangeControl
                                            value={boxShadowVertical}
                                            onChange={data =>
                                                setAttributes({
                                                    [`${PREFIX}${controlName}BoxShadowVertical`]: data
                                                })
                                            }
                                            min={-100}
                                            max={100}
                                        />
                                    </ResetButton>
                                </div>
                                <div className="faqmate-mb-16">
                                    <ResLabelControl
                                        label={__('Blur', 'faqmate-blocks')}
                                        requiredProps={{
                                            id,
                                            setAttributes,
                                            attributes
                                        }}
                                        noResBtns={true}
                                    />
                                    <ResetButton
                                        onReset={() =>
                                            setAttributes({
                                                [`${PREFIX}${controlName}BoxShadowBlur`]: ''
                                            })
                                        }
                                        value={boxShadowBlur}
                                    >
                                        <RangeControl
                                            value={boxShadowBlur}
                                            onChange={data =>
                                                setAttributes({
                                                    [`${PREFIX}${controlName}BoxShadowBlur`]: data
                                                })
                                            }
                                            min={0}
                                            max={100}
                                        />
                                    </ResetButton>
                                </div>
                                <ResLabelControl
                                    label={__('Spread', 'faqmate-blocks')}
                                    requiredProps={{
                                        id,
                                        setAttributes,
                                        attributes
                                    }}
                                    noResBtns={true}
                                />
                                <ResetButton
                                    onReset={() =>
                                        setAttributes({
                                            [`${PREFIX}${controlName}BoxShadowSpread`]: ''
                                        })
                                    }
                                    value={boxShadowSpread}
                                >
                                    <RangeControl
                                        value={boxShadowSpread}
                                        onChange={data =>
                                            setAttributes({
                                                [`${PREFIX}${controlName}BoxShadowSpread`]: data
                                            })
                                        }
                                        min={0}
                                        max={100}
                                    />
                                </ResetButton>
                            </div>
                        </Popover>
                    )}
                </FlexItem>
            </Flex>
        </div>
    );
};

export default withInstanceId(BoxShadowControl);
